import React, { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import api, { money, errMsg } from "../lib/api";
import ExecutivePNL from "../components/ExecutivePNL";
import { monthIST } from "../lib/datetime";

export default function ProfitLoss() {
  const [pnlMonth, setPnlMonth] = useState(monthIST());

  // Drilldown modal state (for OPEX voucher inspection)
  const [drill, setDrill] = useState(null);
  const [drillTitle, setDrillTitle] = useState("");

  const openDrill = async (row, title, m) => {
    setDrillTitle(title);
    try {
      const res = await api.get(`/banking/pnl/drill?ledger_id=${row.ledger_id}&month=${m || pnlMonth}`);
      setDrill(res.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-7xl mx-auto">
      {/* Executive Profit & Loss Statement (MIS) */}
      <ExecutivePNL month={pnlMonth} setMonth={setPnlMonth} onDrill={openDrill} />

      {/* Drilldown modal */}
      {drill && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs"
          onClick={() => setDrill(null)}
        >
          <div
            className="bg-white border border-slate-200 rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between bg-slate-50/70">
              <div>
                <h3 className="text-base font-extrabold text-slate-950">
                  {drill.ledger?.code && <span className="text-slate-400 mr-2">{drill.ledger.code}</span>}
                  {drill.ledger?.name}
                </h3>
                <p className="text-xs text-slate-500 mt-1 font-medium">
                  {drillTitle} · {drill.month} · Dr {money(drill.total_debit)} · Cr {money(drill.total_credit)}
                </p>
              </div>
              <button onClick={() => setDrill(null)} className="text-slate-400 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto space-y-6 flex-1">
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
                  Posted vouchers ({drill.entries?.length || 0})
                </h4>
                <table className="lss-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Narration</th>
                      <th className="text-right">Debit</th>
                      <th className="text-right">Credit</th>
                      <th>Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(drill.entries || []).map((e, i) => (
                      <tr key={i}>
                        <td className="tabular text-xs">{e.date}</td>
                        <td className="text-xs">{e.narration}</td>
                        <td className="text-right tabular text-xs">{e.debit ? money(e.debit) : "—"}</td>
                        <td className="text-right tabular text-xs">{e.credit ? money(e.credit) : "—"}</td>
                        <td className="text-xs text-slate-500">{e.source || e.created_by || "—"}</td>
                      </tr>
                    ))}
                    {!(drill.entries || []).length && (
                      <tr>
                        <td colSpan={5} className="text-center text-xs text-slate-400 py-4 italic">
                          No posted vouchers this month
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
                  Bank statement lines mapped to this ledger ({drill.statement_lines?.length || 0})
                  {drill.pending_lines > 0 && (
                    <span className="ml-2 text-amber-700 normal-case">
                      ({drill.pending_lines} still awaiting approval)
                    </span>
                  )}
                </h4>
                <table className="lss-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Narration</th>
                      <th>Ref</th>
                      <th className="text-right">Debit</th>
                      <th className="text-right">Credit</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(drill.statement_lines || []).map((s, i) => (
                      <tr key={i}>
                        <td className="tabular text-xs">{s.date}</td>
                        <td className="text-xs">{s.narration}</td>
                        <td className="text-xs text-slate-500">{s.ref || "—"}</td>
                        <td className="text-right tabular text-xs">{s.debit ? money(s.debit) : "—"}</td>
                        <td className="text-right tabular text-xs">{s.credit ? money(s.credit) : "—"}</td>
                        <td>
                          <span
                            className={`lss-badge text-[10px] ${
                              s.status === "approved"
                                ? "bg-emerald-100 text-emerald-900"
                                : "bg-amber-100 text-amber-900"
                            }`}
                          >
                            {s.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {!(drill.statement_lines || []).length && (
                      <tr>
                        <td colSpan={6} className="text-center text-xs text-slate-400 py-4 italic">
                          No statement lines mapped yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
